import React, { useState } from "react";
import { Box, Avatar, Accordion, AccordionSummary, AccordionDetails } from "@mui/material";
import {
  AccessTime as ClockIcon,
  Today as CalendarIcon,
  AttachMoney as DollarIcon,
  ExpandMore as ExpandMoreIcon,
} from "@mui/icons-material";
import { Text } from "@clipboard-health/ui-react";

import { TimelineEvent } from "./type";
import { Shift } from "@src/interface";

export function KeyMetricsAccordion({ events, shift }: { events: TimelineEvent[]; shift: Shift }) {
  const [expandedMetrics, setExpandedMetrics] = useState(true);
  const timeToBook = (() => {
    const createEvent = events.find((e) => e.type === "creation");
    const bookEvent = events.find((e) => e.type === "booked");
    if (createEvent && bookEvent) {
      const diffMs = bookEvent.time.getTime() - createEvent.time.getTime();
      const diffMinutes = Math.round(diffMs / (1000 * 60));
      if (diffMinutes < 60) {
        return `${diffMinutes} minutes`;
      } else {
        const hours = Math.floor(diffMinutes / 60);
        const minutes = diffMinutes % 60;
        return `${hours}h ${minutes > 0 ? `${minutes}m` : ""}`;
      }
    }
    return "N/A";
  })();

  const onTimePerformance = (() => {
    const clockInEvent = shift.clockInOut;
    if (!clockInEvent?.start || !shift.start) {
      return "N/A";
    }
    const scheduledTime = new Date(shift.start).getTime();
    const actualTime = new Date(clockInEvent.start).getTime();
    const diffMinutes = Math.round((actualTime - scheduledTime) / (1000 * 60));

    if (diffMinutes <= 5) {
      return "On time";
    }

    return diffMinutes <= 15 ? "Late clock-in" : "Very Late";
  })();

  return (
    <Accordion
      expanded={expandedMetrics}
      onChange={() => setExpandedMetrics(!expandedMetrics)}
      sx={{ bgcolor: "#ffffff" }}
    >
      <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={{ bgcolor: "#f5f9ff" }}>
        <Text variant="subtitle1" bold>
          Key Metrics
        </Text>
      </AccordionSummary>
      <AccordionDetails>
        <Box>
          <Box
            sx={{
              padding: "8px 0",
              display: "flex",
              alignItems: "center",
              borderBottom: "1px solid #e0e0e0",
            }}
          >
            <Avatar
              sx={{
                bgcolor: "#e3f2fd",
                width: 36,
                height: 36,
                marginRight: 2,
              }}
            >
              <ClockIcon sx={{ color: "#1976d2" }} />
            </Avatar>
            <Box>
              <Text variant="body2" color="secondary">
                Time to Book
              </Text>
              <Text variant="body1" bold>
                {timeToBook}
              </Text>
            </Box>
          </Box>

          <Box
            sx={{
              padding: "8px 0",
              display: "flex",
              alignItems: "center",
              borderBottom: "1px solid #e0e0e0",
            }}
          >
            <Avatar
              sx={{
                bgcolor: "#e8f5e9",
                width: 36,
                height: 36,
                marginRight: 2,
              }}
            >
              <DollarIcon sx={{ color: "#2e7d32" }} />
            </Avatar>
            <Box>
              <Text variant="body2" color="secondary">
                Rate Difference
              </Text>
              <Text variant="body1" bold>
                0.00
              </Text>
            </Box>
          </Box>

          <Box
            sx={{
              padding: "8px 0",
              display: "flex",
              alignItems: "center",
              borderBottom: "1px solid #e0e0e0",
            }}
          >
            <Avatar
              sx={{
                bgcolor: "#fff3e0",
                width: 36,
                height: 36,
                marginRight: 2,
              }}
            >
              <ClockIcon sx={{ color: "#ed6c02" }} />
            </Avatar>
            <Box>
              <Text variant="body2" color="secondary">
                On-Time Performance
              </Text>
              <Text variant="body1" bold>
                {onTimePerformance}
              </Text>
            </Box>
          </Box>

          {shift.createdFrom && (
            <Box
              sx={{
                padding: "8px 0",
                display: "flex",
                alignItems: "center",
              }}
            >
              <Avatar
                sx={{
                  bgcolor: "#e3f2fd",
                  width: 36,
                  height: 36,
                  marginRight: 2,
                }}
              >
                <CalendarIcon sx={{ color: "#1976d2" }} />
              </Avatar>
              <Box>
                <Text variant="body2" color="secondary">
                  Creation Source
                </Text>
                <Text variant="body1" bold sx={{ textTransform: "capitalize" }}>
                  {shift.createdFrom.toLowerCase()}
                </Text>
              </Box>
            </Box>
          )}
        </Box>
      </AccordionDetails>
    </Accordion>
  );
}
